/**
 * Registry of layouts whose server-side creation is still in flight.
 *
 * The workspace controller's tree mutations (create/duplicate) and the
 * per-layout content store's saves and loads travel on INDEPENDENT serial
 * queues, so a freshly created layout's first PUT (or its loadLayout GET)
 * can reach the server before the create commits. A PUT that wins that
 * race upserts a partial doc which the create's unconditional Put then
 * overwrites, silently dropping the first panel write; a GET that wins
 * returns null and leaves the store revision-blind.
 *
 * createLayout/duplicateNode register their created layout ids here with a
 * promise settled when the create mutation completes (success OR failure).
 * The layout store awaits `waitForPendingCreate` before its first server
 * calls, serializing the two queues for exactly the ids that need it.
 * Layout ids are globally unique (`lay_` + uuid), so the map is unscoped.
 */

const pending = new Map<string, Promise<void>>()

/**
 * Upper bound on how long a save/load will wait on an unsettled create
 * before proceeding anyway (normal error handling then applies). Guards
 * against a wedged mutation queue blocking layout persistence forever.
 * The controller retries offline mutations only on the next user action,
 * so an unsettled create can legitimately outlive this window; timing out
 * just restores today's unordered behavior for that save.
 */
const PENDING_CREATE_TIMEOUT_MS = 10_000

/**
 * Register layout ids created by an in-flight tree mutation. `settled`
 * must settle (either way) when the mutation's server call completes;
 * entries self-clean at that point.
 */
export function registerPendingCreate(layoutIds: string[], settled: Promise<void>): void {
  const entry: Promise<void> = settled
    .catch(() => undefined)
    .finally(() => {
      for (const id of layoutIds) {
        if (pending.get(id) === entry) pending.delete(id)
      }
    })
  for (const id of layoutIds) pending.set(id, entry)
}

/**
 * Resolves once `layoutId`'s pending creation (if any) has committed, or
 * after PENDING_CREATE_TIMEOUT_MS, whichever comes first. Immediate for
 * layouts with no pending creation, which is every layout except ones
 * created moments ago in this tab.
 */
export function waitForPendingCreate(layoutId: string): Promise<void> {
  const entry = pending.get(layoutId)
  if (!entry) return Promise.resolve()
  return Promise.race([
    entry,
    new Promise<void>((resolve) => setTimeout(resolve, PENDING_CREATE_TIMEOUT_MS)),
  ])
}

/** Test hygiene only: drop all registrations between test cases. */
export function clearPendingCreates(): void {
  pending.clear()
}
